<template>
  <fieldset class="form-checkbox">
    <input
      :id="id"
      v-model="computedValue"
      type="checkbox"
      :value="nativeValue"
      :disabled="isDisabled"
      class="form-checkbox-input"
      :aria-labelledby="ariaLabelledby"
    />
    <label
      :for="id"
      :class="[
        'form-checkbox-label',
        position,
        { stretched: isStretched, small: isSmall }
      ]"
    >
      <slot />
    </label>
  </fieldset>
</template>

<script>
export default {
  name: 'Checkbox',
  props: {
    /**
     * Binding value of v-model
     *
     * @type {String, Number, Boolean, Array}
     */
    value: {
      type: [String, Number, Boolean, Array]
    },
    /**
     * Native value of checkbox when using with grouped checkboxes
     *
     * @type {String, Number, Boolean, Array}
     */
    nativeValue: {
      type: [String, Number, Boolean, Array]
    },
    /**
     * Unique ID for each Checkbox so that input and label are mapped to make custom checkbox work
     *
     * @type {String}
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Is checkbox's position left or right?
     *
     * @type {String}
     */
    position: {
      type: String,
      default: 'left',
      validator: function (value) {
        return ['left', 'right'].includes(value)
      }
    },
    /**
     * Label and checkbox can be stretched to edges
     *
     * @type {Boolean}
     */
    isStretched: {
      type: Boolean,
      default: false
    },
    /**
     * Is checkbox disabled
     *
     * @type {Boolean}
     */
    isDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Accessibility label to establish relationship between the checkbox and control label
     *
     * @type {String}
     */
    ariaLabelledby: {
      type: String,
      default: ''
    },
    /**
     * Is checkbox's size small size?
     *
     * @type {Boolean}
     */
    isSmall: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      newValue: this.value
    }
  },
  computed: {
    computedValue: {
      get() {
        return this.newValue
      },
      set(value) {
        this.newValue = value
        this.$emit('input', value)
      }
    }
  },
  watch: {
    // When v-model change, set internal value.
    value(value) {
      this.newValue = value
    }
  }
}
</script>

<style lang="scss">
.form-checkbox {
  margin-bottom: 5px;

  .form-checkbox-input {
    position: absolute;
    opacity: 0;
    visibility: hidden;

    &:focus {
      box-shadow: none;
    }

    &:not(:checked) + label:after {
      opacity: 0;
    }

    &:checked + label:after {
      opacity: 1;
    }

    &:disabled + label:before,
    &:disabled:checked + label:before {
      border: 1px solid $grey-light;
      background: $grey-light;
      color: $grey-dark;
      cursor: not-allowed;
    }
  }

  .form-checkbox-label {
    position: relative;
    padding-left: 30px;
    cursor: pointer;
    display: inline-block;
    user-select: none;
    font-weight: 300;
    min-height: 24px;
    line-height: 24px;

    &:before,
    &:after {
      content: '';
      width: 24px;
      height: 24px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0;
    }

    &.right {
      padding-left: 0;

      &:before,
      &:after {
        left: auto;
        right: -50px;
      }
    }

    &.stretched {
      display: block;
      text-align: right;

      &.right {
        text-align: left;

        &:before,
        &:after {
          left: auto;
          right: 0;
        }
      }
    }

    &.small {
      padding-left: 22px;

      &:before,
      &:after {
        width: 16px;
        height: 16px;
      }
    }

    &:before {
      border: 1px solid $grey;
    }

    &:after {
      background-image: url('assets/checkmark.svg');
      color: $black;
    }
  }
}
</style>
